import { useState, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import gsap from 'gsap';

interface CyberpunkLoaderProps {
  onComplete: () => void;
}

export function CyberpunkLoader({ onComplete }: CyberpunkLoaderProps) {
  const { t } = useI18n();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'init' | 'loading' | 'complete'>('init');

  useEffect(() => {
    const timeline = gsap.timeline({
      onUpdate: () => {
        setProgress(timeline.progress() * 100);
      },
      onComplete: () => {
        setPhase('complete');
        gsap.delayedCall(0.3, () => {
          onComplete();
        });
      },
    });

    setPhase('init');
    timeline.to({}, { duration: 0.8 });

    setPhase('loading');
    timeline.to({}, { duration: 1.2 });

    return () => { timeline.kill(); };
  }, [onComplete]);

  const spinDuration = 3 / 1000000;

  return (
    <div
      className="cyberpunk-loader"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        overflow: 'hidden',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        className="spinner-container"
        style={{
          position: 'relative',
          width: 120,
          height: 120,
          marginBottom: '2rem',
        }}
      >
        <svg
          viewBox="0 0 120 120"
          style={{
            width: '100%',
            height: '100%',
            transform: 'rotate(-90deg)',
          }}
        >
          <defs>
            <linearGradient id="spinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--accent-primary)" />
              <stop offset="50%" stopColor="var(--accent-secondary)" />
              <stop offset="100%" stopColor="var(--accent-tertiary)" />
            </linearGradient>
          </defs>
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="var(--border-color)"
            strokeWidth="3"
          />
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="url(#spinGradient)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="314"
            strokeDashoffset="314"
            style={{
              animation: `spin ${spinDuration}s linear infinite, dash 1.5s ease-in-out infinite`,
            }}
          />
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes dash {
              0% { stroke-dashoffset: 314; stroke-dasharray: 10; }
              50% { stroke-dashoffset: 0; stroke-dasharray: 200; }
              100% { stroke-dashoffset: -314; stroke-dasharray: 10; }
            }
          `}</style>
        </svg>

        <div
          className="core-pulse"
          style={{
            position: 'absolute',
            inset: 20,
            borderRadius: '50%',
            background: 'radial-gradient(circle, var(--accent-primary) 0%, transparent 70%)',
            opacity: 0.6,
            animation: 'pulse 1s ease-in-out infinite',
          }}
        />
      </div>

      <div
        className="loader-text"
        style={{
          textAlign: 'center',
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        <p
          className="phase-text"
          style={{
            fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
            color: 'var(--accent-primary)',
            marginBottom: '0.5rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            minHeight: '1.5em',
          }}
        >
          {phase === 'init' && t('loader.initializing')}
          {phase === 'loading' && t('loader.loading')}
          {phase === 'complete' && t('loader.complete')}
        </p>

        <div
          className="progress-bar"
          style={{
            width: '200px',
            maxWidth: '80vw',
            height: '2px',
            background: 'var(--border-color)',
            borderRadius: '1px',
            overflow: 'hidden',
            margin: '0 auto',
          }}
        >
          <div
            className="progress-fill"
            style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
              borderRadius: '1px',
              transition: 'width 0.1s linear',
            }}
          />
        </div>

        <p
          className="progress-percent"
          style={{
            marginTop: '0.75rem',
            fontSize: '0.75rem',
            color: 'var(--fg-muted)',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {Math.round(progress)}%
        </p>
      </div>

      <div
        className="scanlines"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
          pointerEvents: 'none',
          opacity: 0.3,
        }}
      />
    </div>
  );
}